/**
 * Footer-total utilities for the DataList component.
 *
 * Centralises two concerns shared by the column resolver, the orchestrating
 * hook, and the table renderer:
 *
 * 1. {@link applyColumnTotalOverrides} — overlays externally-supplied,
 *    authoritative per-column totals (`DataListConfig.columnTotalOverrides`)
 *    onto the natively-computed sums.
 * 2. {@link formatColumnTotal} — formats a total value using the same
 *    number/currency formatting as the column's cells.
 *
 * @see Spec section 6.5 (Column Totals)
 */

import type {
  ColumnConfig,
  CurrencyColumnOptions,
  NumberColumnOptions,
} from '../types';
import { formatCurrency, formatNumber } from './cellRenderer';

/**
 * Returns `true` when an override value should replace the computed sum.
 *
 * An explicit finite number — **including `0`** — wins. `undefined`, `null`,
 * and `NaN` are treated as "no override", so the column falls through to its
 * native computed total.
 */
export function isValidTotalOverride(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Merges authoritative per-column total overrides onto a map of
 * natively-computed column totals.
 *
 * Only keys already present in `computedTotals` (i.e. columns that opted into
 * a footer total via `total: true` / `total: { … }`) are eligible — an
 * override for a column without a native total is ignored, mirroring the rule
 * that a column with `total` falsy shows no footer cell.
 *
 * Each eligible key takes the override value when it is a valid finite number
 * ({@link isValidTotalOverride}); otherwise the computed sum is preserved.
 *
 * The input maps are never mutated.
 *
 * @param computedTotals - Natively-computed sums keyed by column key.
 * @param overrides      - Optional authoritative totals keyed by column key.
 * @returns A new totals map with valid overrides applied.
 */
export function applyColumnTotalOverrides(
  computedTotals: Record<string, number>,
  overrides?: Record<string, number>,
): Record<string, number> {
  if (!overrides) return computedTotals;

  const merged: Record<string, number> = { ...computedTotals };

  for (const key of Object.keys(merged)) {
    const override = overrides[key];
    if (isValidTotalOverride(override)) {
      merged[key] = override;
    }
  }

  return merged;
}

/**
 * Formats a footer-total value using the same formatting the column applies
 * to its cells, so an injected override and a native sum render identically.
 *
 * Resolution order:
 * - A `total: { format }` directive (when present) is the most specific
 *   total formatter → {@link formatNumber} with that format. It wins over a
 *   number column's own `typeOptions.format`, since it is declared *for the
 *   total*.
 * - `currency` columns → {@link formatCurrency} with the column's
 *   {@link CurrencyColumnOptions}.
 * - `number` columns → {@link formatNumber} with the column's
 *   {@link NumberColumnOptions}.
 * - Otherwise → `String(value)` (the historical, unformatted behaviour).
 *
 * @param value  - The total value (override or computed sum).
 * @param column - The resolved column config (with its `key`).
 * @returns The formatted display string.
 */
export function formatColumnTotal(
  value: number,
  column: ColumnConfig,
): string {
  // A `total: { format }` directive is the most specific total formatter.
  if (typeof column.total === 'object' && column.total.format) {
    return formatNumber(value, { format: column.total.format });
  }

  const type = column.type ?? 'text';

  if (type === 'currency') {
    return formatCurrency(value, column.typeOptions as CurrencyColumnOptions | undefined);
  }

  if (type === 'number') {
    return formatNumber(value, column.typeOptions as NumberColumnOptions | undefined);
  }

  return String(value);
}
